import React from 'react';
import Layout from '../../components/Layout';
import Icon from '../../components/AppIcon';

const PressPage = () => {
  const pressReleases = [
    {
      date: '2024-12-01',
      title: 'United Empresas Expands to Miami Market',
      summary: 'Company announces launch of operations in Miami, Florida, bringing total active markets to four.',
      type: 'Expansion',
    },
    {
      date: '2024-10-15',
      title: 'UE Reaches 10,000 Active Vendors Milestone',
      summary: 'Platform celebrates significant growth milestone with 10,000 verified service providers across Latin America and the US.',
      type: 'Milestone',
    },
    {
      date: '2024-08-20',
      title: 'United Empresas Closes $2.4M Seed Round',
      summary: 'Company secures funding to accelerate platform development and market expansion.',
      type: 'Funding',
    },
    {
      date: '2024-06-10',
      title: 'Partnership with Dominican Government for Business Formalization',
      summary: 'UE partners with MICM to support micro-business registration and formalization initiatives.',
      type: 'Partnership',
    },
  ];

  const mediaFeatures = [
    { outlet: 'TechCrunch', topic: 'Latin America Marketplace Innovation' },
    { outlet: 'Forbes', topic: 'Top Startups to Watch' },
    { outlet: 'El Caribe', topic: 'Digital Transformation in DR' },
    { outlet: 'El Nuevo Día', topic: 'Puerto Rico Tech Ecosystem' },
  ];

  const brandAssets = [
    { name: 'Logo Package', description: 'Primary and secondary logos in various formats', icon: 'Image' },
    { name: 'Brand Guidelines', description: 'Colors, typography, and usage rules', icon: 'BookOpen' },
    { name: 'Product Screenshots', description: 'High-resolution app and dashboard images', icon: 'Monitor' },
    { name: 'Executive Photos', description: 'Professional headshots of leadership team', icon: 'Users' },
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="py-20 bg-white">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-sm font-medium text-accent uppercase tracking-wider mb-4">
                Media & Press
              </p>
              <h1 className="text-hero text-foreground mb-6">
                Press Room
              </h1>
              <p className="text-body-large mb-8">
                Access press releases, brand assets, and media resources for coverage
                of United Empresas and our mission.
              </p>
              <a
                href="mailto:press@unitedempresaas.com"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white font-medium rounded-brand-sm hover:bg-primary-light transition-colors"
              >
                <Icon name="Mail" size={16} />
                Contact PR Team
              </a>
            </div>

            <div className="bg-muted rounded-brand-lg p-8">
              <h3 className="text-lg font-semibold text-foreground mb-6">
                Media Contact
              </h3>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Icon name="Mail" size={20} className="text-primary" />
                  <div>
                    <div className="text-sm text-muted-foreground">Email</div>
                    <div className="font-medium text-foreground">press@unitedempresaas.com</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Icon name="Phone" size={20} className="text-primary" />
                  <div>
                    <div className="text-sm text-muted-foreground">Phone</div>
                    <div className="font-medium text-foreground">+1 (305) 555-0124</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Icon name="Clock" size={20} className="text-primary" />
                  <div>
                    <div className="text-sm text-muted-foreground">Response Time</div>
                    <div className="font-medium text-foreground">Within 24 hours</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Press Releases */}
      <section className="py-20 bg-muted">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <div className="mb-12">
            <h2 className="text-display text-foreground mb-4">
              Press Releases
            </h2>
            <p className="text-muted-foreground">
              Latest news and announcements from United Empresas.
            </p>
          </div>

          <div className="space-y-6">
            {pressReleases.map((release, index) => (
              <article key={index} className="bg-white rounded-brand p-6 border border-border hover:border-primary/20 hover:shadow-corporate-md transition-all duration-300">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-sm text-muted-foreground">
                        {new Date(release.date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </span>
                      <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs font-medium rounded">
                        {release.type}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      {release.title}
                    </h3>
                    <p className="text-muted-foreground">
                      {release.summary}
                    </p>
                  </div>
                  <button className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary hover:bg-primary/5 rounded-brand-sm transition-colors whitespace-nowrap">
                    Read more
                    <Icon name="ArrowRight" size={14} />
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Media Coverage */}
      <section className="py-20 bg-white">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-display text-foreground mb-4">
              Media Coverage
            </h2>
            <p className="text-muted-foreground">
              Featured in leading publications across the Americas.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {mediaFeatures.map((feature, index) => (
              <div key={index} className="text-center p-6 bg-muted rounded-brand">
                <div className="text-xl font-semibold text-foreground mb-2">
                  {feature.outlet}
                </div>
                <div className="text-sm text-muted-foreground">
                  {feature.topic}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand Kit */}
      <section className="py-20 bg-muted">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <div className="mb-12">
            <h2 className="text-display text-foreground mb-4">
              Brand Kit
            </h2>
            <p className="text-muted-foreground">
              Download official brand assets for press coverage.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {brandAssets.map((asset, index) => (
              <div key={index} className="bg-white rounded-brand p-6 border border-border">
                <div className="w-12 h-12 bg-primary/10 rounded-brand-sm flex items-center justify-center mb-4">
                  <Icon name={asset.icon} size={24} className="text-primary" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">{asset.name}</h3>
                <p className="text-sm text-muted-foreground mb-4">{asset.description}</p>
                <button className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary-light transition-colors">
                  <Icon name="Download" size={14} />
                  Download
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Facts */}
      <section className="py-20 bg-primary">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-display text-white mb-4">
              Company Facts
            </h2>
            <p className="text-white/70">
              Key information for press coverage.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white/10 backdrop-blur rounded-brand p-6 text-center">
              <div className="text-3xl font-semibold text-white mb-2">2023</div>
              <div className="text-sm text-white/70">Founded</div>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-brand p-6 text-center">
              <div className="text-3xl font-semibold text-white mb-2">4</div>
              <div className="text-sm text-white/70">Active Markets</div>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-brand p-6 text-center">
              <div className="text-3xl font-semibold text-white mb-2">12,400+</div>
              <div className="text-sm text-white/70">Vendors</div>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-brand p-6 text-center">
              <div className="text-3xl font-semibold text-white mb-2">$28M</div>
              <div className="text-sm text-white/70">GMV Processed</div>
            </div>
          </div>
        </div>
      </section>

      {/* Editorial Guidelines */}
      <section className="py-20 bg-white">
        <div className="max-w-narrow mx-auto px-4 lg:px-8">
          <h2 className="text-display text-foreground mb-8 text-center">
            Editorial Guidelines
          </h2>
          <div className="bg-muted rounded-brand p-8">
            <h3 className="text-lg font-semibold text-foreground mb-4">
              Company Name Usage
            </h3>
            <ul className="space-y-3 text-muted-foreground">
              <li className="flex items-start gap-2">
                <Icon name="Check" size={16} className="text-accent mt-0.5" />
                <span><strong>Correct:</strong> United Empresas, UE</span>
              </li>
              <li className="flex items-start gap-2">
                <Icon name="X" size={16} className="text-error mt-0.5" />
                <span><strong>Incorrect:</strong> United Empresaas, UnitedEmpresas, U.E.</span>
              </li>
            </ul>

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-4">
              Boilerplate
            </h3>
            <p className="text-muted-foreground">
              United Empresas is building the marketplace infrastructure for Latin America's
              service economy. The platform connects service providers with customers through
              a unified system for discovery, booking, payments, and business management.
              Founded in 2023, United Empresas operates in the Dominican Republic, New York,
              Puerto Rico, and Miami, with plans for continued expansion across the Americas.
            </p>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default PressPage;
